/**
 * Pure helpers for timeseries relayout: numeric range capture and tick refresh.
 *
 * Side effects remain in {@link ScatterPlotComponent}; this module computes patches.
 *
 * @packageDocumentation
 * @module TimeseriesAxisRelayout
 * @public
 */

import { TimeseriesAxisLabelMode } from '../config/plotTypes';
import {
  EpochMillisecondRange,
  TimeseriesAxisTicks,
  TIMESERIES_XAXIS_HOVERFORMAT,
  epochRangeMsToIsoRange,
  parseRelayoutXRangeMs,
  rebuildTimeseriesAxisTicks,
} from './timeseriesAxisTicks';

// -- type definitions --

/** Input for relayout-side pure computation. */
export interface ComputeTimeseriesRelayoutUpdateInput {
  readonly event: Readonly<Record<string, unknown>>;
  readonly displayTimezone: string;
  readonly labelMode: TimeseriesAxisLabelMode;
}

/** Pure relayout result consumed by the React component edge. */
export interface TimeseriesRelayoutUpdate {
  readonly rangeMs: EpochMillisecondRange;
  readonly isoRange: readonly [string, string];
  readonly ticks: TimeseriesAxisTicks;
  readonly layoutStatePatch: Partial<Plotly.Layout>;
}

// -- helper functions --

/**
 * Reads one axis range from a Plotly relayout event.
 *
 * @param event - Plotly relayout payload.
 * @param layoutId - Plotly layout id such as `xaxis` or `yaxis2`.
 * @returns String range pair when present.
 */
export function extractRelayoutAxisRange(
  event: Readonly<Record<string, unknown>>,
  layoutId: string
): [string, string] | undefined {
  const start = event[`${layoutId}.range[0]`];
  const end = event[`${layoutId}.range[1]`];

  if (start === undefined || end === undefined) {
    return undefined;
  }

  return [`${start}`, `${end}`];
}

/**
 * Returns whether a relayout event changed at least one y-axis range.
 *
 * @param event - Plotly relayout payload.
 * @param yLayoutIds - Plotly y-axis layout ids to inspect.
 * @returns `true` when any listed y-axis range changed.
 */
export function relayoutEventHasYAxisRangeChange(
  event: Readonly<Record<string, unknown>>,
  yLayoutIds: readonly string[]
): boolean {
  return yLayoutIds.some(
    (layoutId) => extractRelayoutAxisRange(event, layoutId) !== undefined
  );
}

/**
 * Returns whether a timeseries relayout changed only the horizontal axis.
 *
 * @param event - Plotly relayout payload.
 * @param yLayoutIds - Plotly y-axis layout ids to inspect.
 * @returns `true` for x-only zoom or pan events.
 */
export function isHorizontalOnlyTimeseriesRelayout(
  event: Readonly<Record<string, unknown>>,
  yLayoutIds: readonly string[]
): boolean {
  const xRangeChanged = parseRelayoutXRangeMs(event) !== undefined;
  const yRangeChanged = relayoutEventHasYAxisRangeChange(event, yLayoutIds);

  return xRangeChanged && !yRangeChanged;
}

/**
 * Builds the layout-state patch for x-axis range and manual ticks.
 *
 * @param rangeMs - New visible x-axis range in epoch milliseconds.
 * @param ticks - Regenerated manual tick payload.
 * @returns Partial layout state to merge into component state.
 */
export function buildTimeseriesXAxisLayoutStatePatch(
  rangeMs: EpochMillisecondRange,
  ticks: TimeseriesAxisTicks
): Partial<Plotly.Layout> {
  return {
    xaxis: {
      type: 'date',
      hoverformat: TIMESERIES_XAXIS_HOVERFORMAT,
      range: [rangeMs[0], rangeMs[1]],
      tickmode: 'array',
      tickvals: [...ticks.tickvals],
      ticktext: [...ticks.ticktext],
    },
  };
}

// -- main --

/**
 * Computes the full timeseries relayout update from a Plotly event.
 *
 * @param input - Relayout event plus display timezone and label mode.
 * @returns Numeric range, ISO callback range, ticks, and layout patch.
 */
export function computeTimeseriesRelayoutUpdate(
  input: ComputeTimeseriesRelayoutUpdateInput
): TimeseriesRelayoutUpdate {
  const rangeMs = parseRelayoutXRangeMs(input.event);
  if (!rangeMs) {
    throw new Error('Relayout event missing x-axis range');
  }

  const ticks = rebuildTimeseriesAxisTicks({
    visibleRangeMs: rangeMs,
    displayTimezone: input.displayTimezone,
    labelMode: input.labelMode,
  });
  const isoRange = epochRangeMsToIsoRange(rangeMs);
  const layoutStatePatch = buildTimeseriesXAxisLayoutStatePatch(
    rangeMs,
    ticks
  );

  return {
    rangeMs,
    isoRange,
    ticks,
    layoutStatePatch,
  };
}
